import { inject, Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SessionService } from '../shared/services/session.service';

@Injectable({ providedIn: 'root' })
export class ContextService {
  breadCrumbs: string[] = [];

  private router = inject(Router);
  private sessionService = inject(SessionService);

  constructor() {
    //Initialize breadcrumbs from current URL (treat right outlet as one path)
    const tree = this.router.parseUrl(this.router.url);
    const right = tree.root.children['right'];
    if (right?.segments?.length) {
      this.breadCrumbs = [right.segments.map(s => s.path).join('/')];
    }

    //force default side panel when there's NO right outlet already
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
      const t = this.router.parseUrl(this.router.url);
      const hasRight = !!t.root.children['right'];

      const primary = t.root.children['primary'];
      const primaryPath = primary ? primary.segments.map(s => s.path).join('/') : '';

      if (primaryPath === 'tour' && !hasRight) {
        // Use replaceUrl so we don't add extra history entries
        this.router.navigate([{ outlets: { right: ['project'] } }], { replaceUrl: true });
      }
    });
  }

  openMainView(path: string) {
    this.router.navigate([{ outlets: { left: path } }], { onSameUrlNavigation: 'reload' });
  }

  openContextView(path: string | string[], overrideCurrent = true): boolean {
    const cmd = Array.isArray(path) ? path : path.split('/');

    //Special handling for the project LIST (not details)
    if (cmd[0] === 'project' && cmd.length === 1) {
      const last = this.breadCrumbs.at(-1);
      const alreadyOnProjectList = last === 'project';
      if (alreadyOnProjectList) return true;
      this.breadCrumbs = []; //reset the stack when going to the list
    }

    const crumb = cmd.join('/');
    if (overrideCurrent) this.breadCrumbs.push(crumb);

    this.router.navigate([{ outlets: { right: cmd } }]);
    return true;
  }

  closeContextView() {
    this.breadCrumbs.pop();
    const view = this.breadCrumbs.at(-1) ?? null;
    this.navigateRightScreenTo(view);
  }

  closeAllContextViews() {
    let view: string | undefined;
    let lastView: string | null = null;
    while ((view = this.breadCrumbs.pop()) !== undefined) {
      //keep popping assignment contexts; stop on the first non-assignment view
      if (!view.startsWith('assignment/')) {
        lastView = view;
        break;
      }
    }
    this.navigateRightScreenTo(lastView);
  }

  closeAllDriverContextViews() {
    let view: string | undefined;
    let lastNonDriverView: string | null = null;
    while ((view = this.breadCrumbs.pop()) !== undefined) {
      if (!view.startsWith('masterdata/drivers')) {
        lastNonDriverView = view;
        break;
      }
    }
    this.navigateRightScreenTo(lastNonDriverView);
  }

  closeAllLoadunitContextViews() {
    let view: string | undefined;
    let lastNonLoadunitView: string | null = null;
    while ((view = this.breadCrumbs.pop()) !== undefined) {
      if (!view.startsWith('masterdata/loadunits')) {
        lastNonLoadunitView = view;
        break;
      }
    }
    this.navigateRightScreenTo(lastNonLoadunitView);
  }

  private navigateRightScreenTo(view: string | null) {
    //Default to 'project' only if the user has access; otherwise close the panel
    let cmd: string[] | null;

    if (!view) {
      const canProject = this.sessionService.isAdmin || this.sessionService.isAzubi || this.sessionService.isDispo;
      cmd = canProject ? ['project'] : null;
    } else {
      cmd = view.split('/');
    }

    this.router.navigate([{ outlets: { right: cmd } }]);
  }
}
